import { useEffect, useState } from "react";
import { FileBarChart2, Eye, FileDown, Printer, Mail, RotateCw, Loader2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import Modal from "../components/Modal";
import { inputCls, todayIso } from "../utils/datetime";

const STATUS_STYLE = {
  sent: "bg-teal-soft text-teal-brand",
  generated: "bg-gold-soft/70 text-pine-deep",
  failed: "bg-red-50 text-red-500",
};

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [genDate, setGenDate] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [sendingId, setSendingId] = useState("");
  const [viewHtml, setViewHtml] = useState(null);
  const [viewMeta, setViewMeta] = useState(null);

  const load = () => api.get("/connect/reports").then((r) => setReports(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async (send) => {
    setBusy(true);
    try {
      const r = await api.post("/connect/reports/generate", { date: genDate || undefined, send });
      toast.success(send ? `Report generated and emailed to ${(r.data.recipients || []).join(", ")}` : "Report generated from live records");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate the report");
    }
    setBusy(false);
  };

  const openView = async (rep) => {
    try {
      const r = await api.get(`/connect/reports/${rep.id}`);
      setViewMeta(r.data);
      setViewHtml(r.data.html);
    } catch { toast.error("Could not load the report"); }
  };

  const download = async (rep) => {
    try {
      const r = await api.get(`/connect/reports/${rep.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/html" }));
      const a = document.createElement("a");
      a.href = url; a.download = `daily-report-${rep.report_date}.html`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch { toast.error("Could not download the report"); }
  };

  const printReport = async (rep) => {
    try {
      const r = await api.get(`/connect/reports/${rep.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/html" }));
      const w = window.open(url, "_blank");
      if (w) w.addEventListener("load", () => w.print());
    } catch { toast.error("Could not open the report for printing"); }
  };

  const resend = async (rep) => {
    setSendingId(rep.id);
    try {
      const r = await api.post(`/connect/reports/${rep.id}/send`);
      toast.success(`Report emailed to ${(r.data.recipients || []).join(", ")}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email could not be sent");
    }
    setSendingId("");
  };

  return (
    <div data-testid="connect-reports" className="space-y-5">
      <div>
        <h2 className="font-display text-2xl font-semibold text-pine-deep">Reports — Daily Management Report</h2>
        <p className="text-sm text-slate-500">Automatically emailed every day at 9:00 PM IST. Generate any date manually below.</p>
      </div>

      <div className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-bold text-pine/60">Report Date</label>
          <input type="date" data-testid="report-date" value={genDate} max={todayIso()} onChange={(e) => setGenDate(e.target.value)} className={`mt-1 ${inputCls} w-48`} />
        </div>
        <button data-testid="report-generate-btn" onClick={() => generate(false)} disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-pine px-5 py-2.5 text-xs font-bold text-ivory hover:bg-pine-deep transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileBarChart2 className="h-4 w-4" />} Generate Report
        </button>
        <button data-testid="report-generate-send-btn" onClick={() => generate(true)} disabled={busy}
          className="inline-flex items-center gap-2 rounded-full border border-teal-brand/30 bg-teal-soft px-5 py-2.5 text-xs font-bold text-teal-brand hover:bg-teal-brand hover:text-white transition-colors disabled:opacity-50">
          <Mail className="h-4 w-4" /> Generate &amp; Email
        </button>
        <p className="text-[11px] text-slate-400 self-center flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Recipients are managed in Settings → Report Recipients</p>
      </div>

      <div className="rounded-2xl bg-white border border-pine/8 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="border-b border-pine/8 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <th className="px-5 py-3.5 font-bold">Report Date</th>
                <th className="px-5 py-3.5 font-bold">Generated</th>
                <th className="px-5 py-3.5 font-bold">By</th>
                <th className="px-5 py-3.5 font-bold">Recipients</th>
                <th className="px-5 py-3.5 font-bold">Status</th>
                <th className="px-5 py-3.5 font-bold"></th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} data-testid={`report-row-${r.report_date}`} className="border-b border-pine/5 last:border-0 hover:bg-cream/50">
                  <td className="px-5 py-3.5 font-bold text-pine-deep">{r.report_date_label || r.report_date}</td>
                  <td className="px-5 py-3.5 text-xs text-slate-500">{r.generated_at_label || "-"}</td>
                  <td className="px-5 py-3.5 text-xs text-slate-500">{r.generated_by || "-"}</td>
                  <td className="px-5 py-3.5 text-xs text-slate-500">{(r.recipients || []).join(", ")}</td>
                  <td className="px-5 py-3.5">
                    <span className={`rounded-full px-3 py-1 text-[10px] font-bold ${STATUS_STYLE[r.status] || STATUS_STYLE.generated}`}>
                      {r.status}{r.retry_count ? ` · ${r.retry_count} retr${r.retry_count === 1 ? "y" : "ies"}` : ""}
                    </span>
                    {r.error && <p className="mt-1 text-[10px] text-red-400 max-w-[180px] truncate" title={r.error}>{r.error}</p>}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5 justify-end">
                      <button data-testid={`report-view-${r.report_date}`} onClick={() => openView(r)} className="text-pine hover:text-teal-brand" aria-label="View report"><Eye className="h-4 w-4" /></button>
                      <button data-testid={`report-download-${r.report_date}`} onClick={() => download(r)} className="text-pine hover:text-teal-brand" aria-label="Download report"><FileDown className="h-4 w-4" /></button>
                      <button data-testid={`report-print-${r.report_date}`} onClick={() => printReport(r)} className="text-pine hover:text-teal-brand" aria-label="Print report"><Printer className="h-4 w-4" /></button>
                      <button data-testid={`report-resend-${r.report_date}`} onClick={() => resend(r)} disabled={sendingId === r.id}
                        className="inline-flex items-center gap-1 rounded-full bg-teal-brand px-2.5 py-1 text-[10px] font-bold text-white hover:bg-pine disabled:opacity-50" aria-label="Email report">
                        {sendingId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />} Email
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {reports.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-slate-400">No reports yet — generate one above, or wait for the automatic 9:00 PM IST email.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {viewHtml && (
        <Modal testid="report-view-modal" wide onClose={() => { setViewHtml(null); setViewMeta(null); }}
          title={`Daily Management Report — ${viewMeta?.report_date_label || ""}`}
          subtitle={`Generated ${viewMeta?.generated_at_label || ""} · Status: ${viewMeta?.status || ""}`}>
          <div className="rounded-xl border border-pine/10 overflow-hidden bg-[#EDF1EE]">
            <iframe title="Daily report preview" srcDoc={viewHtml} className="w-full h-[65vh] bg-white" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="report-modal-download" onClick={() => download(viewMeta)}
              className="inline-flex items-center gap-1.5 rounded-full bg-pine px-4 py-2.5 text-xs font-bold text-ivory hover:bg-pine-deep"><FileDown className="h-3.5 w-3.5" /> Download</button>
            <button data-testid="report-modal-print" onClick={() => printReport(viewMeta)}
              className="inline-flex items-center gap-1.5 rounded-full border border-pine/20 px-4 py-2.5 text-xs font-bold text-pine hover:bg-cream"><Printer className="h-3.5 w-3.5" /> Print</button>
            <button data-testid="report-modal-resend" onClick={() => resend(viewMeta)}
              className="inline-flex items-center gap-1.5 rounded-full bg-teal-brand px-4 py-2.5 text-xs font-bold text-white hover:bg-pine"><Mail className="h-3.5 w-3.5" /> Email Report</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
